import { Equal, EqualNot } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { ToasterContent } from '@/components/sv/toaster-content'
import { cn } from '@/lib/utils'
import type { MetricsOperator } from '@/graphql.types'

const copyToClipboard = (text: string, e: React.MouseEvent) => {
  e.stopPropagation()
  navigator.clipboard.writeText(text).then(() => {
    toast(
      <ToasterContent variant="success">Copied to clipboard</ToasterContent>,
      { position: 'top-right' }
    )
  })
}

const CopyableKey = ({
  keyPath,
  nodeKey,
}: {
  keyPath: string
  nodeKey: string
}) => {
  return (
    <span
      onClick={(e) => copyToClipboard(keyPath, e)}
      className="cursor-pointer hover:bg-background rounded-sm px-1 -mx-1 py-0.5 transition-colors text-foreground/80"
    >
      {nodeKey}
    </span>
  )
}

const CopyableValue = ({
  value,
  className,
}: {
  value: unknown
  className?: string
}) => {
  const rawValue = typeof value === 'string' ? value : String(value ?? '')
  const formattedValue = formatValue(value)

  return (
    <span
      onClick={(e) => copyToClipboard(rawValue, e)}
      className={cn(
        'cursor-pointer hover:bg-background rounded-sm px-1 -mx-1 py-0.5 transition-colors truncate max-w-[500px] inline-block align-middle',
        className
      )}
      title={rawValue}
    >
      {formattedValue}
    </span>
  )
}

type FilterAction = {
  key: string
  value: string
  operator: MetricsOperator
}

type JsonPropertyViewerProps = {
  data: Record<string, unknown>
  onAddFilter?: (action: FilterAction) => void
  searchTerm?: string
}

type JsonNodeProps = {
  keyPath: string
  nodeKey: string
  value: unknown
  depth: number
  onAddFilter?: (action: FilterAction) => void
  searchTerm?: string
}

const getValueColor = (value: unknown): string => {
  if (typeof value === 'string') {
    return 'text-green-600 dark:text-green-400'
  }
  if (typeof value === 'number') {
    return 'text-blue-600 dark:text-blue-400'
  }
  if (typeof value === 'boolean') {
    return 'text-purple-600 dark:text-purple-400'
  }
  if (value === null) {
    return 'text-muted-foreground'
  }
  return ''
}

const formatValue = (value: unknown): string => {
  if (typeof value === 'string') {
    return `"${value}"`
  }
  if (value === null) {
    return 'null'
  }
  return String(value)
}

const isPrimitive = (value: unknown): boolean => {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    value === null
  )
}

const FilterButtons = ({
  keyPath,
  value,
  onAddFilter,
}: {
  keyPath: string
  value: unknown
  onAddFilter?: (action: FilterAction) => void
}) => {
  if (!onAddFilter || !isPrimitive(value)) {
    return null
  }

  const stringValue = value === null ? '' : String(value)

  return (
    <span className="inline-flex gap-1 opacity-0 group-hover/prop:opacity-100 transition-opacity ml-2">
      <button
        type="button"
        className="size-5 rounded-sm flex items-center justify-center cursor-pointer hover:bg-background transition-colors"
        onClick={(e) => {
          e.stopPropagation()
          onAddFilter({
            key: keyPath,
            value: stringValue,
            operator: 'EQUALS',
          })
        }}
      >
        <Equal className="size-4" />
      </button>
      <button
        type="button"
        className="size-5 rounded-sm flex items-center justify-center cursor-pointer hover:bg-background transition-colors"
        onClick={(e) => {
          e.stopPropagation()
          onAddFilter({
            key: keyPath,
            value: stringValue,
            operator: 'DOES_NOT_EQUAL',
          })
        }}
      >
        <EqualNot className="size-4" />
      </button>
    </span>
  )
}

const JsonNode = ({
  keyPath,
  nodeKey,
  value,
  depth,
  onAddFilter,
  searchTerm,
}: JsonNodeProps) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const indent = depth * 16

  if (isPrimitive(value)) {
    return (
      <div
        className="group/prop flex items-center py-0.5 hover:bg-accent/50 rounded-sm px-1 -mx-1"
        style={{ paddingLeft: indent }}
      >
        <CopyableKey keyPath={keyPath} nodeKey={nodeKey} />
        <span className="text-muted-foreground mx-1">:</span>
        <CopyableValue value={value} className={getValueColor(value)} />
        <FilterButtons
          keyPath={keyPath}
          value={value}
          onAddFilter={onAddFilter}
        />
      </div>
    )
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <div className="py-0.5 px-1 -mx-1" style={{ paddingLeft: indent }}>
          <CopyableKey keyPath={keyPath} nodeKey={nodeKey} />
          <span className="text-muted-foreground mx-1">:</span>
          <span className="text-muted-foreground">[]</span>
        </div>
      )
    }

    return (
      <div>
        <div
          className="py-0.5 cursor-pointer hover:bg-accent/50 rounded-sm px-1 -mx-1"
          style={{ paddingLeft: indent }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span className="text-muted-foreground mr-1">
            {isExpanded ? '▼' : '▶'}
          </span>
          <CopyableKey keyPath={keyPath} nodeKey={nodeKey} />
          <span className="text-muted-foreground mx-1">:</span>
          <span className="text-muted-foreground">[{value.length}]</span>
        </div>
        {isExpanded && (
          <div>
            {value.map((item, index) => (
              <JsonNode
                key={index}
                keyPath={`${keyPath}[${index}]`}
                nodeKey={String(index)}
                value={item}
                depth={depth + 1}
                onAddFilter={onAddFilter}
                searchTerm={searchTerm}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  if (typeof value === 'object' && value !== null) {
    const entries = Object.entries(value as Record<string, unknown>)

    if (entries.length === 0) {
      return (
        <div className="py-0.5 px-1 -mx-1" style={{ paddingLeft: indent }}>
          <CopyableKey keyPath={keyPath} nodeKey={nodeKey} />
          <span className="text-muted-foreground mx-1">:</span>
          <span className="text-muted-foreground">{'{}'}</span>
        </div>
      )
    }

    return (
      <div>
        <div
          className="py-0.5 cursor-pointer hover:bg-accent/50 rounded-sm px-1 -mx-1"
          style={{ paddingLeft: indent }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <span className="text-muted-foreground mr-1">
            {isExpanded ? '▼' : '▶'}
          </span>
          <CopyableKey keyPath={keyPath} nodeKey={nodeKey} />
          <span className="text-muted-foreground mx-1">:</span>
          <span className="text-muted-foreground">{`{${entries.length}}`}</span>
        </div>
        {isExpanded && (
          <div>
            {entries.map(([key, val]) => (
              <JsonNode
                key={key}
                keyPath={keyPath ? `${keyPath}.${key}` : key}
                nodeKey={key}
                value={val}
                depth={depth + 1}
                onAddFilter={onAddFilter}
                searchTerm={searchTerm}
              />
            ))}
          </div>
        )}
      </div>
    )
  }

  return null
}

export const JsonPropertyViewer = ({
  data,
  onAddFilter,
  searchTerm,
}: JsonPropertyViewerProps) => {
  const entries = Object.entries(data)

  return (
    <div className="text-xs font-mono">
      {entries.map(([key, value]) => (
        <JsonNode
          key={key}
          keyPath={key}
          nodeKey={key}
          value={value}
          depth={0}
          onAddFilter={onAddFilter}
          searchTerm={searchTerm}
        />
      ))}
    </div>
  )
}
